<template>
  <b-modal
    title="Product details"
    @shown="LoadProduct"
    id="productView"
    hide-footer
    size="lg"
  >
    <div v-if="loading">Loading....</div>

    <div v-else-if="product">
      <div class="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h5 class="mb-1">{{ product.name }}</h5>
          <b-badge v-if="product.parentId" class="me-1" variant="light">
            Variant
          </b-badge>
          <b-badge
            :variant="product.isActive ? 'success' : 'secondary'"
            :class="statusBadge(product.isActive)"
          >
            {{ product.isActive ? "Active" : "Inactive" }}
          </b-badge>
        </div>
        <div class="text-end">
          <div class="fs-5 fw-bold">{{ formatMoney(product.unitPrice) }}</div>
          <small class="text-muted"
            >Cost: {{ formatMoney(product.costPrice) }}</small
          >
        </div>
      </div>

      <b-row class="mb-2">
        <b-col cols="6">
          <div class="text-muted small">SKU</div>
          <div>{{ product.sku || "—" }}</div>
        </b-col>
        <b-col cols="6">
          <div class="text-muted small">Barcode</div>
          <div>{{ product.barcode || "—" }}</div>
        </b-col>
      </b-row>

      <b-row class="mb-2">
        <b-col cols="6">
          <div class="text-muted small">Category</div>
          <div>{{ product.category || "—" }}</div>
        </b-col>
        <b-col cols="6">
          <div class="text-muted small">Tax / VAT rate</div>
          <div>{{ product.taxRate ?? 0 }}%</div>
        </b-col>
      </b-row>

      <div v-if="product.variantAttributes" class="mb-2">
        <div class="text-muted small">Attributes</div>
        <div>{{ formatAttributes(product.variantAttributes) }}</div>
      </div>

      <hr />

      <div class="mb-3">
        <div class="text-muted small mb-1">Stock — {{ selectedStoreName }}</div>
        <b-row>
          <b-col cols="4">
            <div class="text-muted small">On hand</div>
            <div class="fw-bold">{{ product.stock?.quantity ?? 0 }}</div>
          </b-col>
          <b-col cols="4">
            <div class="text-muted small">Reserved</div>
            <div class="fw-bold">{{ product.stock?.reserved ?? 0 }}</div>
          </b-col>
          <b-col cols="4">
            <div class="text-muted small">Available</div>
            <div class="fw-bold">{{ product.stock?.available ?? 0 }}</div>
          </b-col>
        </b-row>
      </div>

      <div v-if="product.variantCount > 0">
        <div class="text-muted small mb-1">
          Variants ({{ product.variantCount }})
        </div>
        <div v-if="loadingVariants">Loading variants...</div>
        <b-table
          v-else
          :items="variants"
          :fields="variantFields"
          small
          responsive
        >
          <template #cell(variantAttributes)="data">
            {{ formatAttributes(data.value) }}
          </template>
          <template #cell(unitPrice)="data">
            {{ formatMoney(data.value) }}
          </template>
          <template #cell(stock)="data">
            {{ data.value?.available ?? "—" }}
          </template>
        </b-table>
      </div>
    </div>

    <div v-else class="text-muted">Product not found.</div>
  </b-modal>
</template>

<script>
import ApiService from "@/core/services/api.service";

export default {
  name: "ProductView",
  props: ["productId", "selectedStoreName", "storeId"],
  data() {
    return {
      product: null,
      variants: [],
      loading: true,
      loadingVariants: false,
      variantFields: [
        { key: "sku", label: "SKU" },
        { key: "name", label: "Name" },
        { key: "variantAttributes", label: "Attributes" },
        { key: "unitPrice", label: "Price" },
        { key: "stock", label: "Available" },
      ],
    };
  },
  methods: {
    formatMoney(value) {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: "GHS",
      }).format(value || 0);
    },
    formatAttributes(attrs) {
      if (!attrs || typeof attrs !== "object") return "—";
      return Object.entries(attrs)
        .map(([k, v]) => `${k}: ${v}`)
        .join(", ");
    },
    async LoadProduct() {
      this.loading = true;
      this.variants = [];
      try {
        this.product = await ApiService.get("products/" + this.productId, {
          store_id: this.storeId,
        });

        if (this.product?.variantCount > 0) {
          this.loadVariants();
        }
      } catch (error) {
        console.log(error);
        this.product = null;
      } finally {
        this.loading = false;
      }
    },
    async loadVariants() {
      this.loadingVariants = true;
      try {
        this.variants = await ApiService.get("products", {
          storeId: this.storeId,
          parentId: this.productId,
        });
      } catch (error) {
        console.log(error);
      } finally {
        this.loadingVariants = false;
      }
    },
  },
};
</script>
